import { Box, Divider, Grid2, TextField, Typography } from "@mui/material";

import { CheckboxTextField } from "@/controls/CheckboxTextField";
import { Product } from "@/shared/models/Product";
import { toFraction32 } from "@/utils/fraction";

type Props = {
	product: Product;
};

const SectionHeader = ({ title }: { title: string }) => (
	<Grid2 size={{ xs: 12 }}>
		<Typography
			variant="subtitle2"
			fontWeight={600}
			color="primary"
			sx={{ mb: 0.5, mt: 1 }}
		>
			{title}
		</Typography>
		<Divider />
	</Grid2>
);

const ReadOnlyField = ({
	label,
	value,
	size = { xs: 6, md: 2.4 },
	fraction,
}: {
	label: string;
	value: string | number | null | undefined;
	size?: { xs: number; md?: number };
	// Inches: also show the value to the nearest 1/32" under the field.
	fraction?: boolean;
}) => (
	<Grid2 size={size}>
		<TextField
			label={label}
			value={value ?? ""}
			fullWidth
			size="small"
			disabled
			InputProps={{ readOnly: true }}
			helperText={
				fraction && typeof value === "number" ? toFraction32(value) : undefined
			}
		/>
	</Grid2>
);

const formatBeamTolerance = (
	top: number | null,
	bottom: number | null,
): string => {
	if (top == null && bottom == null) return "";
	const t = top != null ? `+${top}` : "";
	const b = bottom != null ? `−${bottom}` : "";
	return [t, b].filter(Boolean).join(" / ");
};

const formatClipLabel = (p: Product): string => {
	if (p.ClipId == null) return "";
	const partNumber = p.ClipPartNumber ? `${p.ClipPartNumber} - ` : "";
	return `${partNumber}${p.ClipWidth}x${p.ClipHeight} - ${p.HolePattern ?? ""}`;
};

export const WeldlineSetupView = ({ product }: Props) => {
	const isPunched = product.WebHoles === "Yes";

	return (
		<Box sx={{ overflow: "auto", height: "100%", p: 1.5 }}>
			<Grid2 container spacing={1.5} columnSpacing={1.5}>
				{/* Product Info */}
				<SectionHeader title="Product Info" />
				<ReadOnlyField label="Part Number" value={product.PartNumber} />
				<ReadOnlyField
					label="Extended Part #"
					value={product.ExtendedPartNumber}
				/>
				<ReadOnlyField label="Name" value={product.GenericName} />
				<ReadOnlyField
					label="Cust. Part #"
					value={product.CustomerPartNumber}
				/>
				<ReadOnlyField label="Customer" value={product.CustomerName} />
				<ReadOnlyField label="Section" value={product.SectionName} />
				<ReadOnlyField label="Grade" value={product.GradeName} />

				{/* Beam (Saw root) */}
				<SectionHeader title="Beam" />
				<ReadOnlyField
					label="Beam Part #"
					value={product.ParentSawPartNumber}
				/>
				<ReadOnlyField
					label="Beam Cust. Part #"
					value={product.ParentSawCustomerPartNumber}
				/>
				<ReadOnlyField
					label="Length"
					value={product.ParentSawCutLength}
					fraction
				/>
				<ReadOnlyField
					label="Tolerance"
					value={formatBeamTolerance(
						product.ParentSawTopTolerance,
						product.ParentSawBottomTolerance,
					)}
				/>
				<Grid2 size={{ xs: 6, md: 2.4 }}>
					<CheckboxTextField
						label="Punched"
						checked={isPunched}
						onChange={() => {}}
						disabled
					/>
				</Grid2>

				{/* Clip */}
				<SectionHeader title="Clip" />
				<Grid2 size={{ xs: 12, md: 3.8 }}>
					<TextField
						label="Select Clip"
						value={formatClipLabel(product)}
						fullWidth
						size="small"
						disabled
						InputProps={{ readOnly: true }}
					/>
				</Grid2>
				<ReadOnlyField
					label="Width"
					value={product.ClipWidth}
					size={{ xs: 3, md: 1.2 }}
				/>
				<ReadOnlyField
					label="Height"
					value={product.ClipHeight}
					size={{ xs: 3, md: 1.2 }}
				/>
				<ReadOnlyField
					label="Hole Pattern"
					value={product.HolePattern}
					size={{ xs: 6, md: 2 }}
				/>
				<ReadOnlyField
					label="Hole Gage"
					value={product.HoleGage}
					size={{ xs: 6, md: 1.9 }}
				/>
				<ReadOnlyField
					label="Side Gage"
					value={product.SideGage}
					size={{ xs: 6, md: 1.9 }}
				/>

				{/* Weld Configuration */}
				<SectionHeader title="Weld Configuration" />
				<ReadOnlyField
					label="Finished Len"
					value={product.FinishedLength}
					size={{ xs: 6, md: 2 }}
					fraction
				/>
				<ReadOnlyField
					label="Cut Length"
					value={product.ParentSawCutLength}
					size={{ xs: 6, md: 2 }}
					fraction
				/>
				<ReadOnlyField
					label="Tolerance"
					value={product.Tolerance}
					size={{ xs: 6, md: 2 }}
				/>
				<ReadOnlyField
					label="Program"
					value={product.Program}
					size={{ xs: 6, md: 2 }}
				/>
				<ReadOnlyField
					label="Clean Cycle"
					value={product.CleaningCycle}
					size={{ xs: 6, md: 2 }}
				/>
				<ReadOnlyField
					label="Test Interval"
					value={product.TestCycleInterval}
					size={{ xs: 6, md: 2 }}
				/>
				<Grid2 size={{ xs: 6, md: 2 }}>
					<CheckboxTextField
						label="Web Holes"
						checked={isPunched}
						onChange={() => {}}
						disabled
					/>
				</Grid2>
				<ReadOnlyField
					label="Entry Keeper"
					value={product.EntryKeeperCode}
					size={{ xs: 6, md: 2.5 }}
				/>
				<ReadOnlyField
					label="Exit Keeper"
					value={product.ExitKeeperCode}
					size={{ xs: 6, md: 2.5 }}
				/>
				<ReadOnlyField
					label="Bottom Keeper"
					value={product.BottomKeeper}
					size={{ xs: 6, md: 2.5 }}
				/>

				{/* Bundle Configuration */}
				<SectionHeader title="Bundle Configuration" />
				<ReadOnlyField
					label="Columns"
					value={product.BundleCol}
					size={{ xs: 4, md: 2 }}
				/>
				<ReadOnlyField
					label="Rows"
					value={product.BundleRow}
					size={{ xs: 4, md: 2 }}
				/>
				<ReadOnlyField
					label="Count"
					value={product.BundleCount}
					size={{ xs: 4, md: 2 }}
				/>
				<ReadOnlyField
					label="Stick Size"
					value={product.StickSize}
					size={{ xs: 4, md: 2 }}
				/>
				<ReadOnlyField
					label="Band Length"
					value={product.BandLength}
					size={{ xs: 4, md: 2 }}
					fraction
				/>
				<ReadOnlyField
					label="Grooved Timber"
					value={product.GroovedTimber}
					size={{ xs: 4, md: 2 }}
				/>

				{/* Comments */}
				<SectionHeader title="Comments" />
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Product Comments"
						value={product.Comments ?? ""}
						fullWidth
						size="small"
						disabled
						multiline
						rows={2}
						InputProps={{ readOnly: true }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<TextField
						label="Weld Comments"
						value={product.WeldComments ?? ""}
						fullWidth
						size="small"
						disabled
						multiline
						rows={2}
						InputProps={{ readOnly: true }}
					/>
				</Grid2>
			</Grid2>
		</Box>
	);
};
